if (!customElements.get('drawer-component')) {
  customElements.define(
    'drawer-component',
    class Drawer extends HTMLElement {
      static MOBILE_QUERY = '(max-width: 1023px)';
      static TRANSITION_MS = 300;
      static globalListenersBound = false;
      static headerObserver = null;

      constructor() {
        super();

        this.activeTrigger = null;
        this.isOpen = false;
        this.isClosing = false;
      }

      connectedCallback() {
        Drawer.initGlobalListeners();
        Drawer.initHeaderObserver();
      }

      static initGlobalListeners() {
        if (Drawer.globalListenersBound) return;

        Drawer.globalListenersBound = true;
        document.addEventListener('click', Drawer.handleDocumentClick);
        document.addEventListener('keydown', Drawer.handleKeydown);
      }

      static initHeaderObserver() {
        if (Drawer.headerObserver) return;

        const headerGroup = document.querySelector('.shopify-section-group-header-group');

        if (!headerGroup) return;

        Drawer.syncHeaderHeight();
        Drawer.headerObserver = new ResizeObserver(() => Drawer.syncHeaderHeight());
        Drawer.headerObserver.observe(headerGroup);
      }

      static syncHeaderHeight() {
        const headerGroup = document.querySelector('.shopify-section-group-header-group');

        if (!headerGroup) return;

        const height = `${headerGroup.offsetHeight}px`;
        const current = document.documentElement.style.getPropertyValue('--header-group-height');

        if (current === height) return;

        document.documentElement.style.setProperty('--header-group-height', height);
      }

      static get isMobile() {
        return window.matchMedia(Drawer.MOBILE_QUERY).matches;
      }

      static handleDocumentClick(event) {
        const backdrop = event.target.closest('[data-drawer-backdrop]');

        if (backdrop) {
          const drawerId = backdrop.getAttribute('data-drawer-backdrop');
          document.getElementById(drawerId)?.close();
          return;
        }

        const closeButton = event.target.closest('[data-drawer-close]');

        if (closeButton) {
          closeButton.closest('drawer-component')?.close();
          return;
        }

        const trigger = event.target.closest('[data-drawer-open]');

        if (!trigger) return;

        const drawerId = trigger.getAttribute('data-drawer-open');
        const isCartDrawer = drawerId === 'cart-drawer';

        if (!Drawer.isMobile && !isCartDrawer) return;

        event.preventDefault();

        const drawer = document.getElementById(drawerId);

        if (!(drawer instanceof Drawer)) return;

        if (drawer.isOpen && drawer.activeTrigger === trigger) {
          drawer.close();
          return;
        }

        Drawer.closeAllExcept(drawer);
        drawer.open(trigger);
      }

      static handleKeydown(event) {
        if (event.key !== 'Escape') return;

        const menuNav = document.querySelector('menu-nav-component');

        if (menuNav?.isSubmenuOpen) {
          menuNav.back();
          return;
        }

        document.querySelector('drawer-component.drawer--open')?.close();
      }

      static closeAllExcept(exceptDrawer) {
        document.querySelectorAll('drawer-component').forEach((drawer) => {
          if (drawer === exceptDrawer || !drawer.isOpen) return;

          drawer.closeImmediate();
        });
      }

      closeImmediate() {
        this.isClosing = false;
        this.classList.remove('drawer--open');
        this.resetActiveTrigger();
        this.hideBackdrop();
        this.finishClose();
      }

      resetActiveTrigger() {
        if (!this.activeTrigger) return;

        this.activeTrigger.setAttribute('aria-expanded', 'false');
        this.activeTrigger = null;
      }

      open(trigger) {
        this.activeTrigger = trigger;
        trigger.setAttribute('aria-expanded', 'true');

        if (this.isOpen) return;

        this.isOpen = true;
        this.removeAttribute('inert');
        this.setAttribute('aria-hidden', 'false');
        document.body.classList.add('drawer-open');

        if (this.id === 'cart-drawer') {
          document.body.classList.add('drawer-open--cart');
        }

        this.showBackdrop();

        requestAnimationFrame(() => {
          this.classList.add('drawer--open');
        });
      }

      close() {
        if (!this.isOpen || this.isClosing) return;

        this.isClosing = true;
        this.resetActiveTrigger();
        this.classList.remove('drawer--open');

        const onTransitionEnd = (event) => {
          if (event.target !== this || event.propertyName !== 'transform') return;

          this.removeEventListener('transitionend', onTransitionEnd);
          this.finishClose();
        };

        this.addEventListener('transitionend', onTransitionEnd);

        setTimeout(() => {
          if (!this.isClosing) return;

          this.removeEventListener('transitionend', onTransitionEnd);
          this.finishClose();
        }, Drawer.TRANSITION_MS + 50);
      }

      finishClose() {
        this.isOpen = false;
        this.isClosing = false;
        this.classList.remove('drawer--open');
        this.setAttribute('aria-hidden', 'true');
        this.setAttribute('inert', '');

        this.hideBackdrop();

        if (!document.querySelector('drawer-component.drawer--open')) {
          document.body.classList.remove('drawer-open');
          document.body.classList.remove('drawer-open--cart');
        }
      }

      get backdrop() {
        if (!this.id) return null;

        return document.querySelector(`[data-drawer-backdrop="${this.id}"]`);
      }

      showBackdrop() {
        const backdrop = this.backdrop;

        if (!backdrop || Drawer.isMobile) return;

        backdrop.hidden = false;
        backdrop.classList.add('drawer-backdrop--visible');
      }

      hideBackdrop() {
        const backdrop = this.backdrop;

        if (!backdrop) return;

        backdrop.classList.remove('drawer-backdrop--visible');
        backdrop.hidden = true;
      }
    },
  );
}

/**
 * Mobile Menu Navigation
 */
if (!customElements.get('mobile-menu-nav')) {
  customElements.define(
    'mobile-menu-nav',
    class MobileMenuNav extends HTMLElement {
      static TRANSITION_MS = 300;

      constructor() {
        super();

        this.stack = ['root'];
        this.isAnimating = false;
        this.drawerObserver = null;

        this.handleClick = this.handleClick.bind(this);
      }

      connectedCallback() {
        this.drawer = this.closest('drawer-component');

        this.addEventListener('click', this.handleClick);

        if (this.drawer) {
          this.drawerObserver = new MutationObserver(() => {
            if (!this.drawer.classList.contains('drawer--open')) {
              this.reset();
            }
          });
          this.drawerObserver.observe(this.drawer, {
            attributes: true,
            attributeFilter: ['class'],
          });
        }
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.handleClick);
        this.drawerObserver?.disconnect();
      }

      get isSubmenuOpen() {
        return this.stack.length > 1;
      }

      getPanel(panelId) {
        return this.querySelector(`[data-menu-panel="${panelId}"]`);
      }

      handleClick(event) {
        const openTrigger = event.target.closest('[data-submenu-open]');

        if (openTrigger && this.contains(openTrigger)) {
          event.preventDefault();
          this.open(openTrigger.getAttribute('data-submenu-open'), openTrigger);
          return;
        }

        const backTrigger = event.target.closest('[data-submenu-back]');

        if (backTrigger && this.contains(backTrigger)) {
          event.preventDefault();
          this.back();
        }
      }

      open(panelId, trigger) {
        if (this.isAnimating) return;

        const currentPanelId = this.stack[this.stack.length - 1];
        const currentPanel = this.getPanel(currentPanelId);
        const targetPanel = this.getPanel(panelId);

        if (!currentPanel || !targetPanel || currentPanelId === panelId) return;

        this.isAnimating = true;
        this.stack.push(panelId);

        if (trigger) {
          trigger.setAttribute('aria-expanded', 'true');
        }

        targetPanel.removeAttribute('hidden');
        targetPanel.removeAttribute('inert');
        targetPanel.classList.add('mobile-menu__panel--enter-from-right');

        currentPanel.classList.remove('mobile-menu__panel--active');

        requestAnimationFrame(() => {
          currentPanel.classList.add('mobile-menu__panel--exit-left');
          targetPanel.classList.remove('mobile-menu__panel--enter-from-right');
          targetPanel.classList.add('mobile-menu__panel--active');
        });

        setTimeout(() => this.finishForward(currentPanel), MobileMenuNav.TRANSITION_MS + 50);
      }

      finishForward(previousPanel) {
        previousPanel.classList.remove(
          'mobile-menu__panel--exit-left',
          'mobile-menu__panel--active',
        );
        previousPanel.setAttribute('hidden', '');
        previousPanel.setAttribute('inert', '');
        this.isAnimating = false;
      }

      back() {
        if (this.isAnimating || this.stack.length <= 1) return;

        this.isAnimating = true;

        const currentPanelId = this.stack.pop();
        const parentPanelId = this.stack[this.stack.length - 1];
        const currentPanel = this.getPanel(currentPanelId);
        const parentPanel = this.getPanel(parentPanelId);

        if (!currentPanel || !parentPanel) {
          this.isAnimating = false;
          return;
        }

        this.resetTriggerStates();

        parentPanel.removeAttribute('hidden');
        parentPanel.removeAttribute('inert');
        parentPanel.classList.add('mobile-menu__panel--enter-from-left');

        currentPanel.classList.remove('mobile-menu__panel--active');

        requestAnimationFrame(() => {
          currentPanel.classList.add('mobile-menu__panel--exit-right');
          parentPanel.classList.remove('mobile-menu__panel--enter-from-left');
          parentPanel.classList.add('mobile-menu__panel--active');
        });

        setTimeout(() => this.finishBack(currentPanel), MobileMenuNav.TRANSITION_MS + 50);
      }

      finishBack(closedPanel) {
        closedPanel.classList.remove(
          'mobile-menu__panel--exit-right',
          'mobile-menu__panel--active',
        );
        closedPanel.setAttribute('hidden', '');
        closedPanel.setAttribute('inert', '');
        this.isAnimating = false;
      }

      resetTriggerStates() {
        this.querySelectorAll('[data-submenu-open][aria-expanded="true"]').forEach((trigger) => {
          trigger.setAttribute('aria-expanded', 'false');
        });
      }

      reset() {
        if (this.isAnimating) return;

        this.stack = ['root'];
        this.resetTriggerStates();

        this.querySelectorAll('[data-menu-panel]').forEach((panel) => {
          const isRoot = panel.getAttribute('data-menu-panel') === 'root';

          panel.classList.remove(
            'mobile-menu__panel--active',
            'mobile-menu__panel--exit-right',
            'mobile-menu__panel--exit-left',
            'mobile-menu__panel--enter-from-left',
            'mobile-menu__panel--enter-from-right',
          );

          if (isRoot) {
            panel.removeAttribute('hidden');
            panel.removeAttribute('inert');
            panel.classList.add('mobile-menu__panel--active');
            return;
          }

          panel.setAttribute('hidden', '');
          panel.setAttribute('inert', '');
        });
      }
    },
  );
}
